import debounce from 'lodash-es/debounce'
import { Field, createField, ValidateFunction } from './field'
import { memoizePromise } from './memoize-promise'

const debug = require('debug')('form-logic:form')

export type EventCallback = () => void

export interface FieldView<T, E> {
  getDebugName(): string
  getValue(): T
  isActive(): boolean
  setValidationError(error: E | null): void
  setValid(valid: boolean): void
  setValidating(validating: boolean): void
  activate(): void
  addEventListener(
    event: 'input' | 'change' | 'blur',
    callback: EventCallback
  ): void
}

interface Form<E> {
  addField<T>(input: FieldView<T, E>, validate: ValidateFunction<T, E>): void
  validate(timeoutInSeconds?: number): Promise<boolean>
}

export function createForm<E>(): Form<E> {
  interface FieldItem<T> {
    field: Field<E>
    view: FieldView<T, E>
  }
  const fields: FieldItem<any>[] = []

  return {
    addField<T>(view: FieldView<T, E>, validate: ValidateFunction<T, E>) {
      const field = createField<T, E>({
        getValue: () => view.getValue(),
        isFocused: () => view.isActive(),
        validate: memoizePromise(validate, { fulfilledOnly: true }),
        debugName: view.getDebugName(),
      })

      field.subscribe(({ validating, valid, validationError }) => {
        view.setValidating(validating)
        view.setValid(valid)
        view.setValidationError(validationError)
      })

      const debouncedUpdate = debounce(() => field.update(), 250, {
        leading: false,
        trailing: true,
      })

      // input happens on every key press so let's debounce the updates
      view.addEventListener('input', debouncedUpdate)

      // not all elements trigger "input" event but "change" should be a safe fallback
      view.addEventListener('change', () => field.update())

      // in case we don't run validations when element is focused let's do it on "blur"
      view.addEventListener('blur', () => field.update())

      fields.push({ field, view })
    },

    async validate(timeoutInSeconds = 30) {
      debug('validate')

      const validated = await Promise.race([
        timeout(timeoutInSeconds * 1000),
        Promise.all(fields.map(item => item.field.validate())),
      ])

      if (Array.isArray(validated)) {
        for (let i = 0; i < validated.length; i++) {
          if (!validated[i]) {
            fields[i].view.activate()
            return false
          }
        }

        return true
      }

      return false
    },
  }
}

function timeout(delay: number) {
  return new Promise((resolve, reject) => {
    setTimeout(() => reject(new Error('Timeout!')), delay)
  })
}
